import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgForm} from '@angular/forms';
import {Subscription} from 'rxjs';
import {first, finalize} from 'rxjs/operators';
import {ContratService} from '@services/admin/contrat/contrat.service';
import {ContratButtonService} from '@services/admin/contrat/contrat.button.service';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {Contrat, TypeDebit, TypeImport} from '@domain/contrat/contrat';
import {TypeAction} from '@share/component/floating-button/floating-button';
import {ConfirmService} from '@share/component/confirmation/confirm.service';
import {TypeCodeErreur} from '@domain/common/http/result';

/**
 * Composant de gestion des informations générales d'un contrat
 *
 * @author Yannick Gauthier
 * @date 18/08/2025
 */
@Component({
    host: {'data-test-id': 'contrat-generalites'},
    selector: 'contrat-generalites',
    templateUrl: './contrat-generalites.component.html'
})
export class ContratGeneralitesComponent implements OnInit, OnDestroy {
    /** Référence au formulaire */
    @ViewChild('contratForm', {static: false}) contratForm: NgForm;
    
    /** Contrat */
    contrat: Contrat = new Contrat({
        idContrat: 0,
        creationAuto: false
    });
    
    /** ID du contrat */
    idContrat: number;
    
    /** ID du fournisseur */
    idFournisseur: number;
    
    /** Types de débit disponibles */
    typesDebit = Object.values(TypeDebit);
    
    /** Indique si on est en création ou en édition */
    isCreation: boolean = true;
    
    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;

    /** Liste des types entités */
    listeTypeEntite: Array<{idTypeEntite: number, libelle: string}> = [];

    /** Liste des statuts */
    listeStatut: Array<{idStatut: number, libelle: string}> = [];

    /** Liste des imports */
    listeImport: Array<{idImport: number, code: string, libelle: string, typeImport: TypeImport, typeDebit: TypeDebit, isSectionTitle:boolean, isSectionItem: boolean}> = [];

    /** Import sélectionné */
    importSelectionne: {idImport: number, code: string, libelle: string, typeImport: TypeImport, typeDebit: TypeDebit} = null;

    /** Liste des types de débit */
    listeTypesDebit: Array<{type: TypeDebit, libelle: string}> = [];

    /** Énumération TypeDebit pour utilisation dans le template */
    TypeImport = TypeImport;

    /** Liste des types de débit carte */
    listeTypesDebitCarte: Array<{type: TypeDebit, libelle: string}> = [
        {type: TypeDebit.SOCIETE, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.societeCarte')},
        {type: TypeDebit.COLLAB, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.collabCarte')}
    ];

    /** Liste des types de débit facture */
    listeTypesDebitFature: Array<{type: TypeDebit, libelle: string}> = [
        {type: TypeDebit.SOCIETE, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.societeFacture')},
        {type: TypeDebit.COLLAB, libelle: this.translateService.instant('admin.frais.fournisseurs.contrat.type.collabFacture')}
    ];

    /** Souscription au formulaire */
    private formSubscription: Subscription;

    /** Indique si le contenu est chargé */
    contenuCharge: boolean = false;

    /**
     * Constructeur
     *
     * @param activatedRoute Route activée
     * @param router Service de routage
     * @param contratService Service de gestion des contrats
     * @param contratButtonService Service de gestion des boutons
     * @param toastrService Service de notification
     * @param translateService Service de traduction
     * @param confirmService Service de confirmation
     */
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private contratService: ContratService,
        private contratButtonService: ContratButtonService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private confirmService: ConfirmService
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération des paramètres de la route
        this.idContrat = +this.activatedRoute.parent.snapshot.params.idContrat;
        this.idFournisseur = +this.activatedRoute.parent.snapshot.queryParams.idFournisseur;

        //Détermination du mode (création ou édition)
        this.isCreation = !this.idContrat || this.idContrat <= 0;

        //Chargement du contrat
        this.loadContrat();

        //Si en mode création
        if (this.isCreation) {
            //Initialisation d'un nouveau contrat
            this.contrat = new Contrat(null);
            this.contrat.idFournisseur = this.idFournisseur;
            this.contrat.creationAuto = false;
        }
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy(): void {
        //Désinscription des souscriptions
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    /**
     * Charge les données du contrat
     */
    loadContrat(): void {
        this.contratService.getContrat(this.idContrat).pipe(first()).subscribe({
            next: result => {
                //initialisation du contrat
                if (!this.isCreation) {
                    this.contrat = result.data.contrat;
                }

                //initialisation des types entités
                this.listeTypeEntite = result.data.listeTypeEntite;

                //initialisation des statuts
                this.listeStatut = result.data.listeStatut;

                //initialisation des imports avec tri et ajout des titres de section
                this.organiserListeImport(result.data.listeImport);

                //initialisation des types de débit
                if (this.contrat?.typeImport === TypeImport.CARTE) {
                    this.listeTypesDebit = this.listeTypesDebitCarte;
                } else {
                    this.listeTypesDebit = this.listeTypesDebitFature;
                }

                //Initialisation des actions après chargement du contrat
                this.initActions();

                //Indique que le contenu est chargé
                this.contenuCharge = true;

            },
            error : () => {
                this.toastrService.error(
                    this.translateService.instant('admin.frais.contrat.error.load'),
                    this.translateService.instant('global.error.title')
                );
            }
        });
    }

    /**
     * Initialisation des actions
     */
    initActions(): void {
        this.contratButtonService.setActions([
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                isVisible: () => this.contratForm?.valid,
                doAction: () => this.saveContrat()
            },
            {
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                isVisible: () => !this.isCreation && this.contrat?.idContrat > 0,
                doAction: () => this.deleteContrat()
            }
        ]);
    }

    /**
     * Sauvegarde le contrat
     */
    saveContrat(): void {
        //Vérification de la validité du formulaire
        if (!this.contratForm.valid) {
            return;
        }
        
        //Mise à jour l'indicateur informant que le traitement est en cours
        this.isSaving = true;
        
        //Sauvegarde du contrat
        this.contratService.saveContrat(this.contrat)
            .pipe(first(), finalize(() => {
                //Mise à jour l'indicateur informant que le traitement est terminé
                this.isSaving = false;
            }))
            .subscribe({
                next: result => {
                    //S'il n'y a pas d'erreur
                    if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                        //Message d'information
                        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                        
                        //Mise à jour du contrat
                        this.contrat = result.data.contrat;
                        
                        //Mise à jour de l'indication d'ajout
                        this.isCreation = false;
                    } else {
                        //Message d'erreur
                        TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                    }
                },
                error: () => {
                    //Message d'erreur
                    this.toastrService.error(this.translateService.instant('global.erreurs.enregistrement'));
                }
            });
    }
    
    /**
     * Supprime le contrat
     */
    deleteContrat(): void {
        //Mise à jour l'indicateur informant que le traitement est en cours
        this.isSaving = true;
        
        //Affichage de la confirmation de suppression du contrat
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(first())
            .subscribe(isConfirmed => {
                //Si l'utilisateur a confirmé
                if (isConfirmed) {
                    //Suppression du contrat
                    this.contratService.deleteContrat(this.contrat)
                        .pipe(first(), finalize(() => {
                            //Mise à jour l'indicateur informant que le traitement est terminé
                            this.isSaving = false;
                        }))
                        .subscribe({
                            next: result => {
                                //S'il n'y a pas d'erreur
                                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                                    //Message d'information
                                    this.toastrService.success(this.translateService.instant('global.success.suppression'));
                                    
                                    //Redirection vers la liste des contrats du fournisseur avec le bon onglet sélectionné
                                    this.router.navigate(['Admin/Frais/Factures/Fournisseurs', this.idFournisseur], {
                                        fragment: 'CONTRAT'
                                    });
                                } else {
                                    //Message d'erreur
                                    TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                                }
                            },
                            error: () => {
                                //Message d'erreur
                                this.toastrService.error(this.translateService.instant('global.erreurs.suppression'));
                            }
                        });
                } else {
                    //Mise à jour l'indicateur informant que le traitement est terminé
                    this.isSaving = false;
                }
            });
    }

    /**
     * Organise la liste des imports par type et ajoute des titres de section
     * @param listeOrigine Liste originale des imports
     */
    organiserListeImport(listeOrigine: Array<{idImport: number, libelle: string, typeImport: string, typeDebit: string}>): void {
        //Réinitialisation de la liste
        this.listeImport = [];

        //Création des sections avec leurs titres
        const sections = [
            {
                titre: 'Carte',
                filtre: (item: any) => item.typeImport === TypeImport.CARTE,
                items: []
            },
            {
                titre: 'Factures entreprise',
                filtre: (item: any) => item.typeImport === TypeImport.FACTURE && item.typeDebit === TypeDebit.SOCIETE,
                items: []
            },
            {
                titre: 'Factures individuelles',
                filtre: (item: any) => item.typeImport === TypeImport.FACTURE && item.typeDebit === TypeDebit.COLLAB,
                items: []
            }
        ];

        //Tri de la liste originale par typeImport et typeDebit
        const listeTriee = [...listeOrigine].sort((a, b) => {
            //D'abord par typeImport
            if (a.typeImport !== b.typeImport) {
                return a.typeImport === TypeImport.CARTE ? -1 : 1;
            }
            //Ensuite par typeDebit si typeImport est identique
            if (a.typeDebit !== b.typeDebit) {
                return a.typeDebit === TypeDebit.SOCIETE ? -1 : 1;
            }
            return 0;
        });

        //Répartition des éléments dans les sections
        listeTriee.forEach(item => {
            for (const section of sections) {
                if (section.filtre(item)) {
                    section.items.push({
                        idImport: item.idImport,
                        code: item.idImport + '_' + item.typeImport,
                        libelle: item.libelle,
                        typeImport: item.typeImport as TypeImport,
                        typeDebit: item.typeDebit as TypeDebit,
                        isSectionTitle: false,
                        isSectionItem: true
                    });
                    break;
                }
            }

        });

        //Construction de la liste finale avec les titres de section
        sections.forEach(section => {
            if (section.items.length > 0) {
                //Ajout du titre de la section
                this.listeImport.push({
                    idImport: 0,
                    code: '0',
                    libelle: section.titre,
                    typeImport: null,
                    typeDebit: null,
                    isSectionTitle: true,
                    isSectionItem: false
                });

                //Ajout des éléments de la section
                this.listeImport.push(...section.items);
            }
        });

        //Recherche de l'import correspondant à idFormatFacture du contrat
        this.trouverImportSelectionne();
    }
    
    /**
     * Trouve l'import correspondant au format du contrat (idFormatCarte ou idFormatFacture selon le type)
     */
    trouverImportSelectionne(): void {
        //Détermination de l'ID du format en fonction du type d'import
        const formatId = this.contrat?.typeImport === TypeImport.CARTE
            ? this.contrat?.idFormatCarte
            : this.contrat?.idFormatFacture;

        if (this.contrat && formatId && this.listeImport && this.listeImport.length > 0) {
            //Recherche de l'élément dans la liste des imports
            const importTrouve = this.listeImport.find(item => 
                !item.isSectionTitle && item.idImport === formatId && item.typeImport === this.contrat.typeImport
            );

            //Si un élément est trouvé, on le stocke dans importSelectionne
            if (importTrouve) {
                this.importSelectionne = {
                    idImport: importTrouve.idImport,
                    code: importTrouve.idImport + '_' + importTrouve.typeImport,
                    libelle: importTrouve.libelle,
                    typeImport: importTrouve.typeImport,
                    typeDebit: importTrouve.typeDebit
                };
            } else {
                this.importSelectionne = null;
            }
        } else {
            this.importSelectionne = null;
        }
    }

    /**
     * Appelé lors d'un changement du type d'import
     */
    onImportChange(): void {
        //Mise à jour des paramètres du contrat
        this.contrat.typeImport = this.importSelectionne?.typeImport;
        this.contrat.typeDebit = this.importSelectionne?.typeDebit;

        //Si le type d'import est Carte
        if (this.contrat.typeImport === TypeImport.CARTE) {
            //La valeur doit être écrite dans idFormatCarte (code_banque en BDD)
            this.contrat.idFormatCarte = this.importSelectionne?.idImport;
            //id_fournisseur doit être null
            this.contrat.idFormatFacture = null;
            //Mise à jour de la liste des types de débit et des statuts
            this.listeTypesDebit = this.listeTypesDebitCarte;
            this.contrat.idStatutValide = null;
            this.contrat.idStatutErreur = null;
        } else {
            //La valeur doit être écrite dans idFormatFacture (id_fournisseur en BDD)
            this.contrat.idFormatFacture = this.importSelectionne?.idImport;
            //code_banque doit être null
            this.contrat.idFormatCarte = null;
            //Mise à jour de la liste des types de débit et de la création automatique
            this.listeTypesDebit = this.listeTypesDebitFature;
            this.contrat.creationAuto = false;
        }
    }
}
